import React, { FunctionComponent, useState } from "react";
import { Form, Tooltip } from "antd";
import { HiOutlineUpload, HiOutlineDocument, HiOutlineTrash, HiOutlineRefresh } from "react-icons/hi";
import FormLabel from "./FormLabel";
import UploadTile from "./UploadTile";
import FileUploadModal from "./FileUploadModal";
import { getFileIconUrl } from "./fileIcons";
import { QuestionComponentProps } from "../helper";
import { buildRules } from "../formUtils";
import { buildFormAssetKey, uploadToS3 } from "./s3Upload";

// Supported file-upload types → HTML `accept` tokens + display labels. "video" allows any video file.
const FILE_TYPE_ACCEPT: Record<string, string> = {
  pdf: ".pdf",
  png: ".png",
  doc: ".doc",
  docx: ".docx",
  ppt: ".ppt",
  pptx: ".pptx",
  xls: ".xls",
  xlsx: ".xlsx",
  csv: ".csv",
  video: "video/*"
};

// Default accepted types for a FILE_UPLOAD question when the question carries no explicit validation.fileTypes.
export const DEFAULT_FILE_UPLOAD_TYPES = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "csv", "video"];

const resolveTypes = (fileTypes?: string[]): string[] => (Array.isArray(fileTypes) && fileTypes.length > 0 ? fileTypes : DEFAULT_FILE_UPLOAD_TYPES);
const buildAccept = (fileTypes?: string[]): string =>
  resolveTypes(fileTypes)
    .map(t => FILE_TYPE_ACCEPT[t.toLowerCase()] || `.${t.toLowerCase()}`)
    .join(",");
const buildAcceptLabel = (fileTypes?: string[]): string =>
  resolveTypes(fileTypes)
    .map(t => (t.toLowerCase() === "video" ? "Video" : t.toUpperCase()))
    .join(", ");

// Last path segment of a stored file URL (query/hash stripped), decoded when possible.
const fileNameFromUrl = (url: string): string => {
  const last = url.split(/[?#]/)[0].split("/").pop() || "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

interface FileUploadInputProps {
  value?: string;
  onChange?: (url: string | undefined) => void;
  disabled?: boolean;
  accept?: string;
  acceptLabel?: string;
  maxFileSize?: number; // bytes
  allowedExtensions?: string[];
  formId?: string;
  questionId?: string;
}

/**
 * Controlled file-upload input. As the direct child of a `Form.Item`, antd injects `value`/`onChange`,
 * so the stored answer is the uploaded file URL (a string). Clicking opens a modal with a drag-drop
 * uploader; the file is uploaded (with progress) on confirm.
 */
const FileUploadInput: FunctionComponent<FileUploadInputProps> = ({
  value,
  onChange,
  disabled,
  accept,
  acceptLabel,
  maxFileSize,
  allowedExtensions,
  formId,
  questionId
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [fileName, setFileName] = useState<string>("");

  const upload = (file: File, onProgress: (percent: number) => void) =>
    uploadToS3(file, buildFormAssetKey("files", formId, questionId, file.name), file.type, onProgress);

  const handleUploaded = (url: string, name: string) => {
    setFileName(name);
    onChange?.(url);
    setModalOpen(false);
  };

  const handleRemove = () => {
    setFileName("");
    onChange?.(undefined);
  };

  // Prefer the uploaded file's name for the extension; fall back to the stored URL (e.g. in read-only preview).
  const fileIconUrl = getFileIconUrl(fileName || value);
  // Restricted uploaders (e.g. off-campus PDF only) name a stored file by its URL's last segment rather than a generic label.
  const shownName = fileName || (allowedExtensions && value ? fileNameFromUrl(value) : "") || "Uploaded file";

  return (
    <>
      {value ? (
        <div className="inline-flex items-center gap-2 text-sm text-neutral-11">
          {fileIconUrl ? (
            <img src={fileIconUrl} alt="" className="flex-shrink-0 h-4 w-4 object-contain" />
          ) : (
            <HiOutlineDocument className="flex-shrink-0 text-neutral-9 text-base" />
          )}
          <span className="truncate max-w-[220px]">{shownName}</span>
          {disabled ? (
            <a href={value} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 ml-1 text-primary-6 hover:underline focus:outline-none">
              View
            </a>
          ) : (
            <>
              <Tooltip title="Remove file">
                <button type="button" onClick={handleRemove} aria-label="Remove file" className="flex-shrink-0 ml-1 focus:outline-none">
                  <HiOutlineTrash className="text-error-1 text-base" />
                </button>
              </Tooltip>
              <Tooltip title="Replace file">
                <button type="button" onClick={() => setModalOpen(true)} aria-label="Replace file" className="flex-shrink-0 focus:outline-none">
                  <HiOutlineRefresh className="text-base" />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      ) : disabled ? (
        <span className="text-sm text-neutral-7">No file found</span>
      ) : (
        <UploadTile icon={<HiOutlineUpload className="text-base" />} label="Upload file" disabled={disabled} onClick={() => setModalOpen(true)} />
      )}

      {modalOpen && (
        <FileUploadModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          accept={accept}
          acceptLabel={acceptLabel}
          maxFileSize={maxFileSize}
          allowedExtensions={allowedExtensions}
          upload={upload}
          onUploaded={handleUploaded}
        />
      )}
    </>
  );
};

// FILE_UPLOAD — learner uploads a file; the answer is the resulting URL.
// `fileTypes` overrides the question's own accepted types (the off-campus form passes ["pdf"]); omit it for the default behaviour.
const FileUpload: FunctionComponent<QuestionComponentProps & { fileTypes?: string[] }> = ({ question, disabled = false, fileTypes }) => {
  const validation = question.validation || {};
  const types = fileTypes && fileTypes.length > 0 ? fileTypes : validation.fileTypes;

  return (
    <Form.Item
      name={question.id}
      label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
      rules={buildRules(question)}
    >
      <FileUploadInput
        disabled={disabled}
        accept={buildAccept(types)}
        acceptLabel={buildAcceptLabel(types)}
        allowedExtensions={fileTypes && fileTypes.length > 0 ? fileTypes.map(t => t.toLowerCase()) : undefined}
        maxFileSize={typeof validation.maxFileSize === "number" ? validation.maxFileSize : undefined}
        formId={question.formId}
        questionId={question.id}
      />
    </Form.Item>
  );
};

export default FileUpload;
